// src/lib/learn/placement-check.ts
//
// "Prove I know this" — a short placement check on a learning-path step.
//
// A student who already knows a step should not have to sit through its lesson
// and a ten-question skill check to move on. So a step that is not yet passed
// offers a placement check: a handful of FRESH questions on the step's topic,
// with the pass rule stated before the first question. Passing marks the step
// Passed (by placement), and the steps waiting on it open. Failing changes
// nothing — the lesson and the skill check are still there, and the student is
// pointed at them.
//
// THE EVIDENCE RULE (PLACEMENT_RULE, below — the copy and the grader both read it)
//   - 5 questions on the step's topic, every one never answered by this student
//     in practice, a test, a review card or an earlier placement check.
//   - A fixed difficulty mix: 1 easy, 2 medium, 2 hard. A pass cannot be made of
//     easy questions alone.
//   - Pass at 4 of 5 (80%) — stricter than the skill check's 70%, because five
//     answers are half the evidence of ten. A blank counts as a miss.
//   - The FIRST answer to each question is the one graded, recorded by the
//     server as it grades (/api/practice/check). No hints before answering.
//   - Offered only while the student has at most CHECKPOINT_COUNT − 5 − 1 (4)
//     answers on the topic: the check adds five, so the total stays under the
//     skill check's ten and a pass can never be overruled the moment it is
//     earned (see below).
//   - ONE try per step. A retry would be another roll of the dice (a student
//     right half the time passes a single try ~19% of the time; unlimited tries
//     eventually always — a pure guesser on four choices, ~1.6% a try). And it
//     is not needed: a missed check's five answers count toward the skill
//     check like any others, so five more answers there and the ordinary rule
//     decides. Nothing is wasted and nothing is taken away.
//   - An unanswered check is not a try: a closed tab resumes the same questions
//     for PLACEMENT_RULE.resumeHours, then is voided.
//
// HOW A PLACEMENT PASS COMPARES TO A SKILL CHECK PASS
//   Same for the path: the step reads Passed, it counts toward the track's
//   "N of M passed", and every step waiting on it unlocks. Different as
//   evidence: it earns no mastery tier (a tier needs ten answers), the step says
//   "Passed by placement", and it holds only until the student has answered a
//   full skill check's worth (ten) of questions on the topic. From then on the
//   ordinary rule (Silver: 70% over ten or more) decides, placement or not —
//   the larger sample wins.
//
// PURE MODULE — client-safe. The db side is placement-store.ts.

import { checkpointHref, findStep, followingSteps } from './learning-path'
import type { PathProgress, PathStepProgress } from './path-progress'

/* ------------------------------------------------------------------ */
/*  The rule                                                           */
/* ------------------------------------------------------------------ */

export type PlacementDifficulty = 'E' | 'M' | 'H'

export const PLACEMENT_RULE = {
  /** Questions per placement check. */
  questions: 5,
  /** Correct answers needed to pass. */
  passAt: 4,
  /** Placement checks per step, ever. A check counts once any question is answered. */
  maxAttempts: 1,
  /** The difficulty mix, served in this order (easy first). Sums to `questions`. */
  mix: { E: 1, M: 2, H: 2 } as Readonly<Record<PlacementDifficulty, number>>,
  /** An unanswered check older than this is void (not an attempt) and starts over. */
  resumeHours: 24,
} as const

const MIX_ORDER: readonly PlacementDifficulty[] = ['E', 'M', 'H']
const DIFFICULTY_WORD: Record<PlacementDifficulty, string> = {
  E: 'easy',
  M: 'medium',
  H: 'hard',
}

/** "1 easy, 2 medium and 2 hard" — the mix as one phrase. */
export function mixPhrase(mix: Readonly<Record<PlacementDifficulty, number>> = PLACEMENT_RULE.mix) {
  const parts = MIX_ORDER.filter((d) => mix[d] > 0).map((d) => `${mix[d]} ${DIFFICULTY_WORD[d]}`)
  return parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`
}

/**
 * The most answers a student may already have on a topic and still be offered a
 * placement check: the check adds `questions` more, and the total must stay
 * under a skill check's worth, or mastery would rule on it the moment it ends.
 */
export function maxPriorAnswers(checkpointCount: number): number {
  return Math.max(0, checkpointCount - PLACEMENT_RULE.questions - 1)
}

/* ------------------------------------------------------------------ */
/*  Copy                                                               */
/* ------------------------------------------------------------------ */

function joinTopics(topics: readonly string[]): string {
  if (topics.length <= 1) return topics.join('')
  return `${topics.slice(0, -1).join(', ')} and ${topics.at(-1)}`
}

/**
 * The one-sentence pass rule shown before the check starts:
 * "Get at least 4 of 5 right to mark Linear Equations in 1 Variable as known and
 * unlock Linear Equations in 2 Variables and Equivalent Expressions."
 */
export function placementRuleSentence(topic: string, unlocks: readonly string[]): string {
  const { passAt, questions } = PLACEMENT_RULE
  const tail = unlocks.length > 0 ? ` and unlock ${joinTopics(unlocks)}` : ' and move ahead'
  return `Get at least ${passAt} of ${questions} right to mark ${topic} as known${tail}.`
}

/** The evidence, spelled out — the bullet list under the rule sentence. */
export function placementEvidenceLines(
  topic: string,
  opts: { checkpointCount: number; gatePct: number },
): string[] {
  const { questions, passAt } = PLACEMENT_RULE
  const more = opts.checkpointCount - questions
  return [
    `${questions} questions on ${topic} you have never answered: ${mixPhrase()}.`,
    `No hints before you answer, and your first answer to each question is the one that counts. A skipped question counts as a miss.`,
    `Pass (${passAt} of ${questions}): the step is marked Passed by placement and the steps after it open. It earns no medal yet: once you have answered ${opts.checkpointCount} questions on ${topic}, the usual rule (${opts.gatePct}% or better) decides.`,
    `Miss it: nothing is taken away. It is one try per step, and your ${questions} answers count toward the skill check: ${more} more there and the usual rule decides.`,
  ]
}

/* ------------------------------------------------------------------ */
/*  Availability                                                       */
/* ------------------------------------------------------------------ */

export type PlacementRecord = {
  topic: string
  /** open = not graded yet; void = abandoned unanswered (not an attempt). */
  status: 'open' | 'passed' | 'failed' | 'void'
  correct: number
  answered: number
  total: number
}

/** Per-step placement facts, as path-progress.ts reports them. Plain JSON. */
export type StepPlacement = {
  /** Show "Prove I know this" on this step. */
  offered: boolean
  /** An unfinished check is waiting — the button resumes it. */
  open: boolean
  attemptsLeft: number
  /** The passing check, when the step was placed out of. */
  passed: { correct: number; total: number } | null
  /** Why it is not offered, for copy; null when offered (or passed). */
  blocked: 'attempts' | 'evidence' | null
}

/** A try is spent once any of its questions has been answered. */
export function countsAsAttempt(r: PlacementRecord): boolean {
  return r.status === 'passed' || r.status === 'failed' || (r.status === 'open' && r.answered > 0)
}

export function stepPlacement(input: {
  records: readonly PlacementRecord[]
  /** The step reads Passed by its mastery tier (a skill check's worth of evidence). */
  passedByMastery: boolean
  attempted: number
  checkpointCount: number
}): StepPlacement {
  const used = input.records.filter(countsAsAttempt).length
  const attemptsLeft = Math.max(0, PLACEMENT_RULE.maxAttempts - used)
  const pass = input.records.find((r) => r.status === 'passed')
  const open = input.records.some((r) => r.status === 'open')
  const passed = pass ? { correct: pass.correct, total: pass.total } : null

  if (input.passedByMastery || passed) {
    return { offered: false, open: false, attemptsLeft, passed, blocked: null }
  }
  // An open check can always be resumed (or scored, when it was answered and
  // left): it was offered when it started.
  if (open) return { offered: true, open: true, attemptsLeft, passed: null, blocked: null }
  if (attemptsLeft === 0) {
    return { offered: false, open: false, attemptsLeft, passed: null, blocked: 'attempts' }
  }
  if (input.attempted > maxPriorAnswers(input.checkpointCount)) {
    return { offered: false, open: false, attemptsLeft, passed: null, blocked: 'evidence' }
  }
  return { offered: true, open: false, attemptsLeft, passed: null, blocked: null }
}

/**
 * The locked steps a pass on `topic` would open right now: those waiting on
 * `topic` and nothing else.
 */
export function unlocksIfPassed(
  topic: string,
  steps: readonly Pick<PathStepProgress, 'topic' | 'state' | 'missing'>[],
): string[] {
  return steps
    .filter((s) => s.state === 'locked' && s.missing.length === 1 && s.missing[0] === topic)
    .map((s) => s.topic)
}

/* ------------------------------------------------------------------ */
/*  Selection                                                          */
/* ------------------------------------------------------------------ */

export type PlacementCandidate = { id: string; difficulty: string | null }

/**
 * Pick the check's questions from the student's FRESH pool on the topic: the
 * mix quota per level, served easy → hard. A level short of questions is made
 * up from the next harder level first (never easier, so a short hard pool does
 * not soften the check), then from whatever is left. Null when the fresh pool
 * cannot fill the check — a seen question is never passed off as fresh.
 */
export function selectPlacementQuestions(
  pool: readonly PlacementCandidate[],
  shuffleFn: <T>(xs: T[]) => T[],
): string[] | null {
  const { questions, mix } = PLACEMENT_RULE
  const unique = [...new Map(pool.map((q) => [q.id, q])).values()]
  if (unique.length < questions) return null

  const byLevel: Record<PlacementDifficulty | 'other', PlacementCandidate[]> = {
    E: [],
    M: [],
    H: [],
    other: [],
  }
  for (const q of shuffleFn([...unique])) {
    const d = q.difficulty
    if (d === 'E' || d === 'M' || d === 'H') byLevel[d].push(q)
    else byLevel.other.push(q)
  }

  const picked: Record<PlacementDifficulty, string[]> = { E: [], M: [], H: [] }
  let short = 0
  for (const level of MIX_ORDER) {
    const take = byLevel[level].splice(0, mix[level])
    picked[level].push(...take.map((q) => q.id))
    short += mix[level] - take.length
  }
  // Fill any shortfall: harder levels first, then untagged, then easy.
  const fillOrder: (PlacementDifficulty | 'other')[] = ['H', 'M', 'other', 'E']
  const extra: string[] = []
  for (const level of fillOrder) {
    while (short > 0 && byLevel[level].length > 0) {
      extra.push(byLevel[level].shift()!.id)
      short--
    }
  }
  if (short > 0) return null
  return [...picked.E, ...picked.M, ...picked.H, ...extra]
}

/* ------------------------------------------------------------------ */
/*  Grading                                                            */
/* ------------------------------------------------------------------ */

export type PlacementGrade = { correct: number; answered: number; total: number; passed: boolean }

/** First answers recorded on the check: { [questionId]: correct }. */
export type PlacementAnswers = Record<string, boolean>

/** Parse the stored answers JSON defensively — anything not a boolean is dropped. */
export function parsePlacementAnswers(raw: unknown): PlacementAnswers {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out: PlacementAnswers = {}
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'boolean') out[k] = v
  }
  return out
}

/**
 * Grade a check: only issued questions count, each once (its first answer), an
 * unanswered one is a miss, and `passAt` is the bar frozen when it was issued.
 */
export function gradePlacement(
  issuedIds: readonly string[],
  answers: PlacementAnswers,
  passAt: number = PLACEMENT_RULE.passAt,
): PlacementGrade {
  const issued = [...new Set(issuedIds)]
  let correct = 0
  let answered = 0
  for (const id of issued) {
    if (!(id in answers)) continue
    answered++
    if (answers[id]) correct++
  }
  return { correct, answered, total: issued.length, passed: correct >= passAt }
}

/* ------------------------------------------------------------------ */
/*  Links                                                              */
/* ------------------------------------------------------------------ */

/** The practice-runner URL for a placement check: an ids session, `from=placement`. */
export function placementHref(input: {
  placementId: string
  questionIds: readonly string[]
  topic: string
}): string {
  const params = new URLSearchParams({
    ids: input.questionIds.join(','),
    label: `Prove it: ${input.topic}`,
    from: 'placement',
    placement: input.placementId,
  })
  return `/practice/session?${params.toString()}`
}

/** The placement id on a runner URL's params, or null when this is not a placement session. */
export function placementIdFrom(from: string | null | undefined, placement: string | null) {
  return from === 'placement' && placement && /^[a-z0-9]{8,40}$/i.test(placement) ? placement : null
}

/* ------------------------------------------------------------------ */
/*  Verdict                                                            */
/* ------------------------------------------------------------------ */

/** What the results screen (and the /learn dialog) is told after grading. Plain JSON. */
export type PlacementVerdict = {
  placementId: string
  topic: string
  lessonSlug: string
  correct: number
  answered: number
  total: number
  passAt: number
  passed: boolean
  /** Nothing was answered: no try spent, nothing recorded. */
  void: boolean
  /** The path now reads the step Passed. */
  stepPassed: boolean
  /** Steps waiting on this one that are now open. */
  unlocked: string[]
  /**
   * Where the path goes next from this step, if anywhere — the Next button.
   * After a pass it is the first step the pass opened (so the button and the
   * "now open" line name the same step), else the first open step that follows
   * this one on the path (followingSteps). See placementNextStep.
   */
  next: { topic: string; lessonSlug: string } | null
  /** The full skill check — the route after a miss. */
  skillCheckHref: string
  /** Answers still needed on the topic before the skill check rule rates it. */
  answersToSkillCheck: number
}

export function buildPlacementVerdict(
  graded: {
    placementId: string
    topic: string
    passAt: number
    void: boolean
    grade: PlacementGrade
  },
  progress: Pick<PathProgress, 'steps' | 'tracks'>,
): PlacementVerdict {
  const step = findStep(graded.topic)
  const row = progress.steps.find((s) => s.topic === graded.topic)
  const unlocked = step
    ? progress.steps
        .filter(
          (s) => s.state === 'available' && findStep(s.topic)?.prerequisites.includes(step.topic),
        )
        .map((s) => s.topic)
    : []
  const passed = graded.grade.passed && !graded.void
  const opened = passed ? unlocked : []
  return {
    placementId: graded.placementId,
    topic: graded.topic,
    lessonSlug: step?.lessonSlug ?? '',
    correct: graded.grade.correct,
    answered: graded.grade.answered,
    total: graded.grade.total,
    passAt: graded.passAt,
    passed,
    void: graded.void,
    stepPassed: row?.state === 'passed',
    unlocked: opened,
    next: placementNextStep(graded.topic, opened, progress),
    skillCheckHref: checkpointHref(graded.topic),
    answersToSkillCheck: Math.max(0, (step?.checkpoint.count ?? 0) - (row?.attempted ?? 0)),
  }
}

/**
 * The verdict's Next step, from the path graph — never from the "Continue"
 * pick (path-progress.ts pickNext), which prefers ANY step already underway
 * anywhere in the track. That is how a pass on Linear Equations in 1 Variable
 * said "Linear Equations in 2 Variables is now open" while its Next button went
 * to Linear Functions (read earlier, so in progress) — a step the one just
 * opened is a prerequisite of.
 *
 *   1. The first step this pass opened, in path order — named first in the copy.
 *   2. Else the first step after this one (followingSteps) that is open and not
 *      yet passed.
 *   3. Else null — the button goes back to the path.
 */
export function placementNextStep(
  topic: string,
  unlocked: readonly string[],
  progress: Pick<PathProgress, 'steps'>,
): { topic: string; lessonSlug: string } | null {
  const first =
    unlocked[0] ??
    followingSteps(topic)
      .map((s) => progress.steps.find((p) => p.topic === s.topic))
      .find((p) => p && (p.state === 'available' || p.state === 'in_progress'))?.topic
  const step = first ? findStep(first) : undefined
  return step ? { topic: step.topic, lessonSlug: step.lessonSlug } : null
}

/** The headline and one line of what the check showed, for a verdict. */
export function placementVerdictCopy(v: PlacementVerdict): { title: string; body: string } {
  const score = `${v.correct} of ${v.total}`
  if (v.void) {
    return {
      title: 'No answers, no result',
      body: `You did not answer any questions, so nothing was recorded and no try was used.`,
    }
  }
  if (v.passed && v.stepPassed) {
    // Names the same step the Next button goes to: the first one opened, or
    // (when the pass opened nothing new) the next open step on the path.
    const opened =
      v.unlocked.length > 0
        ? ` ${joinTopics(v.unlocked)} ${v.unlocked.length === 1 ? 'is' : 'are'} now open.`
        : v.next
          ? ` Next on the path: ${v.next.topic}.`
          : ''
    return {
      title: `${v.topic}: passed by placement`,
      body: `You got ${score} on questions you had never seen, at or above the ${v.passAt}-of-${v.total} bar.${opened}`,
    }
  }
  if (v.passed) {
    // Passed the check, but the student's other answers on the topic already
    // add up to a skill check's worth — and mastery decides from there.
    return {
      title: `${score}: a pass, but your other answers outweigh it`,
      body: `With everything you have answered on ${v.topic}, you now have a skill check's worth of evidence, so your overall accuracy decides this step. Keep practising ${v.topic} to pass it.`,
    }
  }
  const missed = v.total - v.correct
  return {
    title: `${score}: not this time`,
    body: `The bar is ${v.passAt} of ${v.total}, and ${missed} ${missed === 1 ? 'question' : 'questions'} got away, so ${v.topic} is worth the lesson first. Nothing on your path changed, and your answers count toward the skill check${v.answersToSkillCheck > 0 ? `: ${v.answersToSkillCheck} more and it rates the step` : ''}.`,
  }
}
